// /u/:slug — public user profile.
//
// Shows: hero (avatar + name + bio + Follow), activity chart,
// public projects, integrations, agents, followers/following.
//
// Resolves the viewed user from the URL slug (last URL segment).
// Falls back to the current user when the slug matches the user's
// own email-prefix or slug, so /u/<me> renders the same surface as
// /profile but in the public-facing layout.
//
// Slug resolution (in order):
//   1. exact match on `peopleAll[*].slug` if populated
//   2. email-prefix match (e.g. `nika` → `nika@symbols.app`)
//   3. fallback `{ email: <slug>@symbols.app, name: <slug> }` so
//      profile pages can be shared even before the directory
//      hydrates the row.
//
// Activity chart writes to `_profileActivity` (separate root key
// from tickets' `_activityData`) so the two charts on /tickets and
// /u/:slug don't fight over the same store.

// Slug resolution + data loaders live in `functions/userProfileData.js`, not
// here: the workspace shell must be able to re-run them on a same-route
// workspace switch (PROFILE-PAGE-NO-REHYDRATE-ON-SWITCH-1). This page is now
// only the mount-time caller.
import { hydrateUserProfile } from '../functions/userProfileData.js'

export const userProfile = {
  extends: 'Page',
  metadata: (el) => ({ title: el.call('pageTitle', 'account.page.userProfile.title') }),
  maxWidth: 'none',
  padding: '0',

  // Mount-time hydration. The SAME function the shell re-runs on a
  // workspace switch (loadPrivateData.js PHASE 2) — one resolver, two
  // callers, so the two paths can never drift.
  onCreate: async (el, s) => {
    await hydrateUserProfile(s.root)
  },

  onRemove: (el) => {
    const root = el.getRoot()
    if (!root) return
    root.update({
      _profileSlug: null,
      _profileViewed: null,
      _profileActivity: {},
      _profileProjects: null
    })
  },

  // Loader — shown only during the resolution round-trip so neither
  // the real profile nor the 404 chrome flashes for the wrong identity.
  // Hidden via `display: none` (not `if:`) so the page DOM stays mounted
  // and child fetches keep their context.
  ProfileResolving: {
    extends: 'Flex',
    hide: (el, s) => !s.root?._profileLoading,
    flow: 'y',
    align: 'center center',
    minHeight: '40vh',
    // Indeterminate busy state — Spinner, never a "Loading…" string (§9).
    LoadingSpinner: {
      color: 'caption'
    }
  },

  UserProfile: {
    hide: (el, s) => !!s.root?._profileLoading || !!s.root?._profileViewed?._notFound
  },

  // 404 chrome — shown when `_resolveViewedAsync` couldn't match the
  // slug anywhere (local people directory + server username lookup).
  ProfileNotFound: {
    extends: 'Flex',
    if: (el, s) => !s.root?._profileLoading && !!s.root?._profileViewed?._notFound,
    flow: 'y',
    align: 'center center',
    gap: 'B',
    padding: 'D',
    minHeight: '60vh',
    H: {
      tag: 'h1',
      text: '404',
      fontSize: 'F',
      fontWeight: '700',
      color: 'title',
      lineHeight: '1',
      margin: '0'
    },
    P: {
      tag: 'p',
      text: (el, s) => `No user found at /user/${s.root?._profileSlug || ''}`,
      color: 'caption',
      fontSize: 'A',
      margin: '0'
    },
    Link: {
      href: '/team-members',
      text: '{{ account.userProfile.browseAll | polyglot }}',
      color: 'blue',
      fontWeight: '500',
      margin: 'A - -'
    }
  }
}

// Test-only surface — re-exported from `functions/userProfileData.js`, where
// the resolvers now live. Kept on this module so existing importers
// (pages/userProfile.test.js) keep resolving.
export { __userProfileInternals } from '../functions/userProfileData.js'
